import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { LayoutMiniature } from "../components/LayoutMiniature";
import { MediaZone } from "../components/MediaZone";
import { PasswordZone } from "../components/PasswordZone";
import { Ticker } from "../components/Ticker";
import { CardsLayout } from "../components/CardsLayout";
import { ColorPickerField } from "../components/ColorPickerField";
import {
  DEFAULT_CONFIG,
  LAYOUT_MODES,
  LAYOUT_PRESETS,
} from "../lib/constants";
import { ArrowLeft, Tv, UserCog, Settings, PhoneCall, SkipForward, RotateCw, Sparkles, PlayCircle, PauseCircle } from "lucide-react";

/**
 * Public demo page — showcases the platform WITHOUT authentication.
 * Uses local React state (no API calls). Any change is ephemeral.
 *
 * Tabs:
 *  - TV: live preview of the current layout + ticker + auto-called tickets every 15s
 *  - Operador: buttons to call next / manual / recall / skip / reset
 *  - Admin: layout picker + nomenclature + brand + main colors
 */

const seedHistory = [
  { code: "AB-9999", desk: "01" },
  { code: "A-105", desk: "03" },
  { code: "A-104", desk: "02" },
  { code: "A-103", desk: "01" },
];

const initialState = {
  currentTicket: { code: "A001", desk: "03", timestamp: Date.now() },
  history: seedHistory,
  nextNumberByPrefix: { A: 102, B: 101, C: 101, D: 101 },
};

const DemoBanner = () => (
  <div className="bg-amber-500 text-black text-xs md:text-sm font-bold uppercase tracking-[0.2em] text-center py-2 px-4 flex items-center justify-center gap-2" data-testid="demo-banner">
    <Sparkles className="w-4 h-4" />
    <span>Modo Demo · Nenhuma alteração é salva</span>
    <Sparkles className="w-4 h-4" />
  </div>
);

const TVCanvas = ({ state, config, flashRef }) => {
  const { colors, layout, media } = config;
  const layoutKey = layout || LAYOUT_MODES.CARDS;

  const isPasswordWide =
    layoutKey === LAYOUT_MODES.PASSWORD_TOP || layoutKey === LAYOUT_MODES.MEDIA_TOP;
  const isPip = layoutKey === LAYOUT_MODES.PIP_MEDIA_BR;

  const PasswordZoneEl = (
    <PasswordZone
      state={state}
      colors={colors}
      nomenclature={config.nomenclature}
      flashRef={flashRef}
      wideLayout={isPasswordWide}
      hideExtras={isPip}
    />
  );

  const MediaSection = (
    <section className="relative overflow-hidden w-full h-full" data-testid="media-section">
      <MediaZone
        media={media}
        bgColor={colors.mediaBg}
        bgOpacity={colors.mediaBgOpacity ?? 1}
        bgImage={colors.mediaBgImage || ""}
        silentByDefault={true}
      />
    </section>
  );

  const renderLayout = () => {
    if (layoutKey === LAYOUT_MODES.CARDS) {
      return (
        <CardsLayout
          state={state}
          config={config}
          colors={colors}
          media={media}
          flashRef={flashRef}
          silentByDefault={true}
        />
      );
    }
    if (layoutKey === LAYOUT_MODES.FULLSCREEN_PASSWORD) {
      return <div className="h-full w-full">{PasswordZoneEl}</div>;
    }
    if (layoutKey === LAYOUT_MODES.PASSWORD_TOP) {
      return (
        <div className="flex flex-col h-full w-full">
          <div className="h-1/2 relative">{PasswordZoneEl}</div>
          <div className="h-1/2 relative border-t border-border/40">{MediaSection}</div>
        </div>
      );
    }
    if (layoutKey === LAYOUT_MODES.MEDIA_TOP) {
      return (
        <div className="flex flex-col h-full w-full">
          <div className="h-1/2 relative">{MediaSection}</div>
          <div className="h-1/2 relative border-t border-border/40">{PasswordZoneEl}</div>
        </div>
      );
    }
    if (layoutKey === LAYOUT_MODES.PIP_MEDIA_BR) {
      return (
        <div className="relative h-full w-full">
          <div className="absolute inset-0">{PasswordZoneEl}</div>
          <div
            className="absolute bottom-4 right-4 shadow-2xl border-2 border-foreground/20 overflow-hidden"
            style={{ width: "25vw", height: "15vw", minWidth: 240, minHeight: 140 }}
          >
            {MediaSection}
          </div>
        </div>
      );
    }
    // MEDIA_RIGHT (default)
    return (
      <div className="flex flex-row h-full w-full">
        <div className="w-[42%] relative border-r border-border/40">{PasswordZoneEl}</div>
        <div className="w-[58%] relative">{MediaSection}</div>
      </div>
    );
  };

  return (
    <div className="relative bg-background flex flex-col overflow-hidden" style={{ aspectRatio: "16/9" }} data-testid="demo-tv-canvas">
      <div className="flex-1 overflow-hidden">{renderLayout()}</div>
      <Ticker
        messages={config.ticker || []}
        bgColor={colors.tickerBg}
        textColor={colors.tickerText}
        bgOpacity={colors.tickerBgOpacity ?? 1}
        showClock={config.showClock !== false && layoutKey !== LAYOUT_MODES.CARDS}
      />
    </div>
  );
};

const OperatorPanel = ({ state, setState, config }) => {
  const prefixes = config.prefixes || ["A", "B", "C", "D"];
  const desks = config.desks || ["01", "02", "03", "04"];
  const [selectedPrefix, setSelectedPrefix] = useState(prefixes[0]);
  const [selectedDesk, setSelectedDesk] = useState(desks[0]);
  const [manualCode, setManualCode] = useState("");

  const callNext = () => {
    setState((prev) => {
      const nMap = { ...(prev.nextNumberByPrefix || {}) };
      const num = nMap[selectedPrefix] || 101;
      const code = `${selectedPrefix}${String(num).padStart(3, "0")}`;
      nMap[selectedPrefix] = num + 1;
      const ticket = { code, desk: selectedDesk, timestamp: Date.now() };
      const history = [prev.currentTicket, ...(prev.history || [])].filter(Boolean).slice(0, 8);
      return { currentTicket: ticket, history, nextNumberByPrefix: nMap };
    });
  };

  const callManual = () => {
    if (!manualCode.trim()) return;
    setState((prev) => {
      const ticket = { code: manualCode.trim().toUpperCase(), desk: selectedDesk, timestamp: Date.now(), manual: true };
      const history = [prev.currentTicket, ...(prev.history || [])].filter(Boolean).slice(0, 8);
      return { ...prev, currentTicket: ticket, history };
    });
    setManualCode("");
  };

  const recall = () => {
    setState((prev) => ({ ...prev, currentTicket: prev.currentTicket ? { ...prev.currentTicket, timestamp: Date.now() } : null }));
  };

  const skip = () => {
    setState((prev) => {
      if (!prev.currentTicket) return prev;
      const history = [prev.currentTicket, ...(prev.history || [])].slice(0, 8);
      return { ...prev, currentTicket: null, history };
    });
  };

  const reset = () => {
    setState({ currentTicket: null, history: [], nextNumberByPrefix: { A: 101, B: 101, C: 101, D: 101 } });
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="demo-operator">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-foreground/60 mb-4">Chamar próxima senha</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em]">Prefixo</Label>
            <Select value={selectedPrefix} onValueChange={setSelectedPrefix}>
              <SelectTrigger className="mt-2 rounded-none h-11" data-testid="demo-prefix-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {prefixes.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em]">{config.nomenclature || "Guichê"}</Label>
            <Select value={selectedDesk} onValueChange={setSelectedDesk}>
              <SelectTrigger className="mt-2 rounded-none h-11" data-testid="demo-desk-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {desks.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button onClick={callNext} className="w-full h-11 rounded-none uppercase tracking-[0.15em]" data-testid="demo-call-next-btn">
              <PhoneCall className="w-4 h-4 mr-2" /> Chamar
            </Button>
          </div>
        </div>
      </div>

      <div className="border-t border-border pt-6">
        <div className="text-xs uppercase tracking-[0.25em] text-foreground/60 mb-4">Chamar senha manual</div>
        <div className="flex gap-3">
          <Input
            value={manualCode}
            onChange={(e) => setManualCode(e.target.value)}
            placeholder="Ex.: XY-42"
            className="rounded-none h-11 uppercase font-mono"
            data-testid="demo-manual-input"
          />
          <Button onClick={callManual} variant="outline" className="rounded-none h-11 uppercase tracking-[0.15em]" data-testid="demo-manual-call-btn">
            Chamar manual
          </Button>
        </div>
      </div>

      <div className="border-t border-border pt-6 flex flex-wrap gap-3">
        <Button onClick={recall} variant="outline" className="rounded-none h-11 uppercase tracking-[0.15em]" data-testid="demo-recall-btn">
          <PhoneCall className="w-4 h-4 mr-2" /> Repetir chamada
        </Button>
        <Button onClick={skip} variant="outline" className="rounded-none h-11 uppercase tracking-[0.15em]" data-testid="demo-skip-btn">
          <SkipForward className="w-4 h-4 mr-2" /> Pular
        </Button>
        <Button onClick={reset} variant="ghost" className="rounded-none h-11 uppercase tracking-[0.15em] text-destructive" data-testid="demo-reset-btn">
          <RotateCw className="w-4 h-4 mr-2" /> Reiniciar tudo
        </Button>
      </div>

      <div className="border-t border-border pt-6">
        <div className="text-xs uppercase tracking-[0.25em] text-foreground/60 mb-3">Últimas chamadas</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {(state.history || []).slice(0, 8).map((h, i) => (
            <div key={i} className="border border-border/60 p-3 rounded-none flex items-baseline gap-2">
              <span className="font-heading font-black text-xl tracking-tighter">{h.code}</span>
              <span className="text-[10px] uppercase tracking-[0.2em] opacity-60">{(config.nomenclature || "G").charAt(0).toUpperCase()}.{h.desk}</span>
            </div>
          ))}
          {(!state.history || state.history.length === 0) && (
            <div className="text-sm text-foreground/50 col-span-full py-4">Nenhuma chamada ainda.</div>
          )}
        </div>
      </div>
    </div>
  );
};

const AdminPanel = ({ config, setConfig }) => {
  const update = (patch) => setConfig((prev) => ({ ...prev, ...patch }));
  const updateColors = (patch) => setConfig((prev) => ({ ...prev, colors: { ...prev.colors, ...patch } }));

  return (
    <div className="p-6 md:p-8 space-y-8" data-testid="demo-admin">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-foreground/60 mb-4">Layout da TV</div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {LAYOUT_PRESETS.map((val) => {
            const labels = {
              media_right: "Senha · Mídia",
              password_top: "Senha em cima",
              media_top: "Mídia em cima",
              fullscreen_password: "Só Senha",
              pip_media_br: "PiP — Mídia canto",
              cards: "Cards Prioritário",
            };
            const active = config.layout === val;
            return (
              <button
                key={val}
                onClick={() => update({ layout: val })}
                className={`p-3 border transition-all text-left flex flex-col gap-2 ${
                  active ? "border-foreground bg-foreground text-background" : "border-border hover:border-foreground"
                }`}
                data-testid={`demo-layout-${val}`}
              >
                <LayoutMiniature mode={val} active={active} />
                <div className="font-heading text-xs font-black tracking-tight leading-tight">{labels[val] || val}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="border-t border-border pt-6">
        <div className="text-xs uppercase tracking-[0.25em] text-foreground/60 mb-4">Identidade</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em]">Nome da marca</Label>
            <Input value={config.brandName} onChange={(e) => update({ brandName: e.target.value })} className="mt-2 rounded-none h-11" data-testid="demo-brand-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em]">Nomenclatura (Guichê / Consultório…)</Label>
            <Input value={config.nomenclature} onChange={(e) => update({ nomenclature: e.target.value })} className="mt-2 rounded-none h-11" data-testid="demo-nomen-input" />
          </div>
        </div>
      </div>

      <div className="border-t border-border pt-6">
        <div className="text-xs uppercase tracking-[0.25em] text-foreground/60 mb-4">Cores principais</div>
        <ColorPickerField label="Acento da senha atual" value={config.colors.passwordAccent} onChange={(v) => updateColors({ passwordAccent: v })} testId="demo-color-accent" />
        <ColorPickerField label="Acento do histórico" value={config.colors.historyAccent} onChange={(v) => updateColors({ historyAccent: v })} testId="demo-color-history" />
        <ColorPickerField label="Fundo da mídia" value={config.colors.mediaBg} onChange={(v) => updateColors({ mediaBg: v })} testId="demo-color-media-bg" />
        <ColorPickerField label="Fundo do ticker" value={config.colors.tickerBg} onChange={(v) => updateColors({ tickerBg: v })} testId="demo-color-ticker-bg" />
      </div>
    </div>
  );
};

const Demo = () => {
  const [state, setState] = useState(initialState);
  const [config, setConfig] = useState({
    ...DEFAULT_CONFIG,
    brandName: "SUA EMPRESA",
    nomenclature: "Consultório",
    layout: LAYOUT_MODES.CARDS,
  });
  const flashRef = useRef(null);
  const [autoPlay, setAutoPlay] = useState(true);

  // Auto-call new tickets every 12s if autoplay is on
  useEffect(() => {
    if (!autoPlay) return undefined;
    const id = window.setInterval(() => {
      setState((prev) => {
        const prefixes = config.prefixes || ["A", "B", "C", "D"];
        const desks = config.desks || ["01", "02", "03", "04"];
        const p = prefixes[Math.floor(Math.random() * prefixes.length)];
        const d = desks[Math.floor(Math.random() * desks.length)];
        const nMap = { ...(prev.nextNumberByPrefix || {}) };
        const num = nMap[p] || 101;
        const code = `${p}${String(num).padStart(3, "0")}`;
        nMap[p] = num + 1;
        const ticket = { code, desk: d, timestamp: Date.now() };
        const history = [prev.currentTicket, ...(prev.history || [])].filter(Boolean).slice(0, 8);
        return { currentTicket: ticket, history, nextNumberByPrefix: nMap };
      });
      // pulse flash
      if (flashRef.current) {
        flashRef.current.classList.remove("flash-pulse");
        void flashRef.current.offsetWidth;
        flashRef.current.classList.add("flash-pulse");
      }
    }, 12_000);
    return () => window.clearInterval(id);
  }, [autoPlay, config.prefixes, config.desks]);

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col" data-testid="demo-page">
      <DemoBanner />

      <header className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 text-sm hover:underline" data-testid="demo-back-link">
            <ArrowLeft className="w-4 h-4" />
            Voltar ao site
          </Link>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant={autoPlay ? "default" : "outline"}
              className="rounded-none"
              onClick={() => setAutoPlay((v) => !v)}
              data-testid="demo-autoplay-toggle"
              title={autoPlay ? "Pausar simulação automática" : "Retomar simulação automática"}
            >
              {autoPlay ? <><PauseCircle className="w-4 h-4 mr-2" /> Pausar</> : <><PlayCircle className="w-4 h-4 mr-2" /> Simular</>}
            </Button>
            <Link to="/signup">
              <Button size="sm" className="rounded-none" data-testid="demo-signup-cta">
                Criar conta grátis
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl mx-auto w-full px-4 md:px-10 py-6 md:py-10">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.3em] text-foreground/60">Experimente antes de contratar</div>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter leading-none mt-2">
            Demo Interativo
          </h1>
          <p className="text-sm md:text-base text-foreground/70 mt-3 max-w-2xl">
            Explore o painel do operador, ajuste o layout no admin e veja a TV atualizando em tempo real.
            A simulação chama uma nova senha automaticamente a cada 12 segundos.
          </p>
        </div>

        <Tabs defaultValue="tv" className="w-full">
          <TabsList className="rounded-none border-b w-full justify-start gap-4 h-auto bg-transparent p-0">
            <TabsTrigger value="tv" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-foreground data-[state=active]:bg-transparent px-4 py-3 uppercase tracking-[0.15em] text-xs font-semibold" data-testid="demo-tab-tv">
              <Tv className="w-4 h-4 mr-2" /> TV
            </TabsTrigger>
            <TabsTrigger value="operator" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-foreground data-[state=active]:bg-transparent px-4 py-3 uppercase tracking-[0.15em] text-xs font-semibold" data-testid="demo-tab-operator">
              <UserCog className="w-4 h-4 mr-2" /> Operador
            </TabsTrigger>
            <TabsTrigger value="admin" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-foreground data-[state=active]:bg-transparent px-4 py-3 uppercase tracking-[0.15em] text-xs font-semibold" data-testid="demo-tab-admin">
              <Settings className="w-4 h-4 mr-2" /> Admin
            </TabsTrigger>
          </TabsList>

          <TabsContent value="tv" className="pt-6">
            <div className="border-2 border-border shadow-2xl overflow-hidden">
              <TVCanvas state={state} config={config} flashRef={flashRef} />
            </div>
            <p className="text-xs text-foreground/50 mt-3">
              Prévia em 16:9. Em produção, a TV ocupa toda a tela do dispositivo.
            </p>
          </TabsContent>

          <TabsContent value="operator" className="pt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="border border-border">
                <div className="px-6 py-3 border-b border-border text-xs uppercase tracking-[0.25em] text-foreground/60 bg-foreground/5">
                  Painel do Operador
                </div>
                <OperatorPanel state={state} setState={setState} config={config} />
              </div>
              <div className="border border-border">
                <div className="px-6 py-3 border-b border-border text-xs uppercase tracking-[0.25em] text-foreground/60 bg-foreground/5">
                  TV ao vivo
                </div>
                <div className="p-2">
                  <TVCanvas state={state} config={config} flashRef={flashRef} />
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="admin" className="pt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="border border-border">
                <div className="px-6 py-3 border-b border-border text-xs uppercase tracking-[0.25em] text-foreground/60 bg-foreground/5">
                  Configurações da TV
                </div>
                <AdminPanel config={config} setConfig={setConfig} />
              </div>
              <div className="border border-border sticky top-4 self-start">
                <div className="px-6 py-3 border-b border-border text-xs uppercase tracking-[0.25em] text-foreground/60 bg-foreground/5">
                  Preview ao vivo
                </div>
                <div className="p-2">
                  <TVCanvas state={state} config={config} flashRef={flashRef} />
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-12 border-t border-border pt-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-black tracking-tighter mb-3">
            Gostou do que viu?
          </h2>
          <p className="text-foreground/70 mb-6">Crie sua conta em menos de 1 minuto. 14 dias de trial grátis, sem cartão.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/signup">
              <Button size="lg" className="rounded-none h-12 uppercase tracking-[0.2em]" data-testid="demo-signup-footer-btn">
                Criar conta grátis
              </Button>
            </Link>
            <Link to="/termos-de-uso">
              <Button size="lg" variant="outline" className="rounded-none h-12 uppercase tracking-[0.2em]" data-testid="demo-terms-footer-btn">
                Termos de Uso
              </Button>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Demo;
